<template>
  <div
    ref="editorWrap"
    class="mentions-editor-wrap"
    :class="{
      disabled
    }"
    :style="computedStyle"
  >
    <div
      ref="divEditor"
      class="mentions-editor"
      :contenteditable="!disabled"
      :data-placeholder="placeholder"
      data-testid="rich-mentions-editor"
      @input="handleInput"
      @click="captureSelectionRange"
      @mouseup="captureSelectionRange"
      @keydown="handleKeyDown"
      @keyup="handleKeyUp"
      @beforeinput="handleBeforeInput"
      @focus="onInputFocus"
      @blur="onInputBlur"
      @compositionstart="handleCompositionstart"
      @compositionend="handleCompositionend"
      @paste="onPaste"
    />
    <div ref="popper" class="popper-wrap">
      <div v-if="state.measuring">
        <MentionsSelect :options="options" @close="handleClose">
          <template #label="{ data }">
            <slot :data="data" name="label" />
          </template>
        </MentionsSelect>
      </div>
    </div>
    <div class="resize-handle" @mousedown="startResize">
      <BotIcon name="config-size" width="8" height="8" padding="0" />
    </div>
  </div>
</template>

<script setup>
import { createPopper } from '@popperjs/core';
import {
  ref,
  shallowRef,
  reactive,
  onMounted,
  provide,
  toRef,
  nextTick,
  watch,
  computed,
  onBeforeUnmount
} from 'vue';
import { useRoute } from 'vue-router';
import {
  getCursorPosition,
  generateGetBoundingClientRect,
  getOffsetText,
  deleteContentAfterAtSign,
  insertMention
} from './utils';
import KeyCode from './keyCode';
import { MentionsContextKey } from './mentionsTypes';
import MentionsSelect from './components/MentionsSelect.vue';

const props = defineProps({
  styleMap: {
    type: Object,
    default: () => ({
      '@': 'color:var(--mention-theme-color)',
      '&': 'color: rgb(55,171,255)',
      '{': 'color:var(--theme-color)'
    })
  },
  config: {
    type: Object,
    default: () => ({})
  },

  mentionClass: {
    type: String,
    default: 'rich-mention__span'
  },
  placeholder: {
    type: String,
    default: ''
  },

  popperOptions: {
    type: Object,
    default: () => undefined
  },
  transformFn: {
    type: Function,
    default: item => item.label
  },
  modelValue: {
    type: [String, Number],
    default: ''
  },
  height: {
    type: [Number, String]
  },
  maxHeight: {
    type: [Number, String],
    default: 'auto'
  },
  minHeight: {
    type: [Number, String],
    default: '40px'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  maxlength: {
    type: Number,
    default: 0
  }
});

const emit = defineEmits([
  'change',
  'focus',
  'blur',
  'pressenter',
  'select',
  'openDialog',
  'update:modelValue'
]);

const divEditor = ref(null);
const resizedHeight = ref(null);
const computedStyle = computed(() => {
  return {
    height: resizedHeight.value
      ? `${resizedHeight.value}px`
      : typeof props.height === 'number'
      ? `${props.height}px`
      : props.height,
    maxHeight: props.maxHeight,
    minHeight: props.minHeight
  };
});
const route = useRoute();
const popper = ref(null);
const isInput = ref(false);
const popperIns = shallowRef(null);

const resolvedConfig = computed(() => {
  const cfg = props.config || {};
  const inTaskFlowAgent =
    String(route.path || '').includes('taskFlowAgent') ||
    String(route.fullPath || '').includes('taskFlowAgent');
  if (!inTaskFlowAgent || !Object.prototype.hasOwnProperty.call(cfg, '@')) {
    return cfg;
  }
  const rest = { ...cfg };
  delete rest['@'];
  return rest;
});

const prefix = computed(() => Object.keys(resolvedConfig.value));
const lastRange = shallowRef(null);
const lastSelectionRange = shallowRef(null);

const composing = shallowRef(false);
const options = ref([]);
const virtualElement = {
  getBoundingClientRect: generateGetBoundingClientRect(0, 0)
};

const state = reactive({
  value: '',
  measuring: false,
  measureLocation: 0,
  measureText: '',
  measurePrefix: '',
  activeIndex: 0,
  isFocus: false
});

const startMeasure = (measureText, measurePrefix, measureLocation) => {
  Object.assign(state, {
    measuring: true,
    measureText,
    measurePrefix,
    measureLocation,
    activeIndex: 0
  });
};
const stopMeasure = callback => {
  Object.assign(state, {
    measuring: false,
    measureLocation: 0,
    measureText: null
  });
  callback?.();
};

const triggerChange = val => {
  emit('change', val);
};

const resolvedMaxLength = computed(() => {
  const max = Number(props.maxlength);
  return Number.isFinite(max) && max > 0 ? Math.floor(max) : 0;
});

const getEditorTextLength = () => divEditor.value?.innerText?.length || 0;

const getSelectedTextLength = () => {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return 0;
  const range = selection.getRangeAt(0);
  if (!isRangeInEditor(range)) return 0;
  return selection.toString().length;
};

const getRemainingTextLength = (removedLength = 0) => {
  const maxLength = resolvedMaxLength.value;
  if (!maxLength) return Infinity;
  const selectedLength = getSelectedTextLength();
  const nextLength = Math.max(
    0,
    getEditorTextLength() - selectedLength - removedLength
  );
  return Math.max(0, maxLength - nextLength);
};

const limitTextForInsert = (text, removedLength = 0) => {
  const content = String(text || '');
  if (!content || !resolvedMaxLength.value) return content;
  return content.slice(0, getRemainingTextLength(removedLength));
};

const handleBeforeInput = event => {
  if (props.disabled || composing.value || !resolvedMaxLength.value) {
    return;
  }

  const inputType = String(event.inputType || '');
  if (!inputType.startsWith('insert') || inputType === 'insertFromPaste') {
    return;
  }

  const inputText = event.data ?? (inputType === 'insertParagraph' ? '\n' : '');
  if (!limitTextForInsert(inputText)) {
    event.preventDefault();
  }
};

const setFocus = () => {
  if (divEditor.value) {
    divEditor.value.focus();
  }
};

onMounted(() => {
  const popperOptions = toRef(props, 'popperOptions');
  if (popperOptions.value) {
    popperIns.value = createPopper(
      virtualElement,
      popper.value,
      popperOptions.value
    );
  } else {
    popperIns.value = createPopper(virtualElement, popper.value, {
      placement: 'auto',
      strategy: 'fixed'
    });
  }
});

const handleCusClick = item => {
  emit('openDialog', item);
};

const handleCompositionstart = () => {
  composing.value = true;
};
const handleInput = () => {
  if (composing.value || props.disabled) {
    return;
  }
  const content = divEditor.value.innerHTML;
  captureSelectionRange();
  triggerChange(content);
  emit('update:modelValue', content);
};

const handleCompositionend = () => {
  composing.value = false;

  handleInput();
};

const selectOption = option => {
  const { scrollTop } = divEditor.value;
  const selecttion = window.getSelection();
  if (document.activeElement !== divEditor.value) {
    setFocus();

    if (selecttion) {
      selecttion.removeAllRanges();
      selecttion.addRange(lastRange.value);
    }
  }

  if (selecttion) {
    const range = selecttion.getRangeAt(0);
    const { value } = option;
    const text = props.transformFn(option);

    // Add zero-width spaces before and after the mention text
    const mentionText = `${text}`;
    const removedLength =
      String(state.measurePrefix || '').length +
      String(state.measureText || '').length;
    if (!limitTextForInsert(mentionText, removedLength)) {
      return;
    }

    deleteContentAfterAtSign(range, state.measurePrefix);
    const r = selecttion.getRangeAt(0);

    insertMention(
      r,
      mentionText,
      value,
      props.mentionClass,
      props.styleMap[state.measurePrefix]
    );

    handleInput();
    triggerChange(divEditor.value.innerHTML);
    nextTick(() => {
      divEditor.value.scrollTop = scrollTop;
    });
    composing.value = false;
    stopMeasure();
  }
};

const handleKeyUp = event => {
  captureSelectionRange();
  const { which } = event;

  if (
    [KeyCode.ESC, KeyCode.UP, KeyCode.DOWN, KeyCode.ENTER].indexOf(which) !== -1
  ) {
    return;
  }
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return;

  const range = selection.getRangeAt(0);
  const { startContainer, startOffset } = range;

  let beforeText = '';
  if (startContainer.nodeType === Node.TEXT_NODE) {
    beforeText = startContainer.textContent.slice(0, startOffset);
  }

  if (!beforeText && startOffset === 0) {
    const prevNode = startContainer.previousSibling;
    if (prevNode && prevNode.nodeType === Node.TEXT_NODE) {
      beforeText = prevNode.textContent;
    }
  }

  const lastChar = beforeText.slice(-1);
  const isPrefixChar = prefix.value.includes(lastChar);

  if (isPrefixChar) {
    const innerText = getOffsetText();
    options.value = resolvedConfig.value[lastChar] || [];

    let inMentionSpan = false;
    let currentNode = startContainer;
    while (currentNode && currentNode !== divEditor.value) {
      if (currentNode.classList?.contains(props.mentionClass)) {
        inMentionSpan = true;
        break;
      }
      currentNode = currentNode.parentNode;
    }

    if (!inMentionSpan) {
      const { x, y } = getCursorPosition();
      virtualElement.getBoundingClientRect = generateGetBoundingClientRect(
        x,
        y
      );
      popperIns.value?.update();
      lastRange.value = range;

      const afterText = innerText.slice(beforeText.length);
      const searchText = afterText.split(/[\s\n]/)[0] || '';

      startMeasure(searchText, lastChar, beforeText.length - 1);
    }
  } else if (state.measuring) {
    stopMeasure();
  }
};

const handleKeyDown = event => {
  const { which } = event;
  if (!state.measuring) {
    if (which === KeyCode.BACKSPACE) {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const { startContainer, startOffset } = range;

        // Check if cursor is before a zero-width space
        if (startContainer.nodeType === Node.TEXT_NODE) {
          const text = startContainer.textContent;

          const charBeforeCursor = text.charAt(startOffset - 1);
          if (charBeforeCursor === '') {
            document.execCommand('delete');
            document.execCommand('delete');
            document.execCommand('delete');
          }
        }
      }
    }
    return;
  }
  if (which === KeyCode.ESC) {
    stopMeasure();
  }
};

const onFocus = event => {
  const { isFocus } = state;
  if (!isFocus && event) {
    emit('focus', event);
  }
  state.isFocus = true;
};
const onBlur = () => {
  const focusTimer = setTimeout(() => {
    state.isFocus = false;
    stopMeasure();
  }, 100);
  clearTimeout(focusTimer);
};

const onInputFocus = event => {
  isInput.value = true;
  composing.value = false;
  onFocus(event);
  captureSelectionRange();
};
const onInputBlur = event => {
  isInput.value = false;
  composing.value = false;
  captureSelectionRange();

  onBlur(event);
};

const setActiveIndex = activeIndex => {
  state.activeIndex = activeIndex;
};

provide(MentionsContextKey, {
  activeIndex: toRef(state, 'activeIndex'),
  setActiveIndex,
  selectOption,
  onFocus,
  onBlur,
  handleCusClick,
  loading: toRef(props, 'loading')
});

const initEditor = ({ html }) => {
  nextTick(() => {
    divEditor.value.innerHTML = html;
  });
};
onMounted(() => {
  divEditor.value.innerHTML = props.modelValue;
});
const handleClose = () => {
  state.measuring = false;
};

const isRangeInEditor = range => {
  if (!range || !divEditor.value) return false;
  const { startContainer } = range;
  return (
    startContainer === divEditor.value ||
    divEditor.value.contains(startContainer)
  );
};

const captureSelectionRange = () => {
  const selection = window.getSelection();
  if (!selection || !selection.rangeCount) return;
  const currentRange = selection.getRangeAt(0);
  if (!isRangeInEditor(currentRange)) return;
  lastSelectionRange.value = currentRange.cloneRange();
};

const createRangeAtEditorEnd = () => {
  if (!divEditor.value) return null;
  const range = document.createRange();
  range.selectNodeContents(divEditor.value);
  range.collapse(false);
  return range;
};

const resolveEditorRange = () => {
  const selection = window.getSelection();
  if (!selection) return null;

  if (selection.rangeCount) {
    const currentRange = selection.getRangeAt(0);
    if (isRangeInEditor(currentRange)) {
      return currentRange;
    }
  }

  if (isRangeInEditor(lastSelectionRange.value)) {
    const cachedRange = lastSelectionRange.value.cloneRange();
    selection.removeAllRanges();
    selection.addRange(cachedRange);
    return cachedRange;
  }

  const endRange = createRangeAtEditorEnd();
  if (!endRange) return null;
  selection.removeAllRanges();
  selection.addRange(endRange);
  return endRange;
};

/** Legacy paste path when insertText is unavailable (undo may not group paste). */
const pastePlainTextWithRange = (range, pastedData) => {
  const selection = window.getSelection();
  if (!selection) {
    return;
  }

  range.deleteContents();

  const currentNode = range.startContainer;
  const { startOffset } = range;
  if (currentNode.nodeType === Node.TEXT_NODE) {
    const currentText = currentNode.textContent;
    const beforeText = currentText.slice(0, startOffset);
    const afterText = currentText.slice(startOffset);
    currentNode.textContent = beforeText + pastedData + afterText;
    const newRange = document.createRange();
    newRange.setStart(currentNode, startOffset + pastedData.length);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
  } else {
    const textNode = document.createTextNode(pastedData);
    range.insertNode(textNode);

    const newRange = document.createRange();
    newRange.setStart(textNode, pastedData.length);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
  }
};

const onPaste = event => {
  if (props.disabled || composing.value) {
    return;
  }

  const { clipboardData } = event;
  if (!clipboardData) {
    return;
  }

  const pastedData = clipboardData.getData('text/plain');
  if (pastedData == null) {
    return;
  }

  event.preventDefault();

  setFocus();
  let range = resolveEditorRange();
  if (!range) {
    return;
  }

  const selection = window.getSelection();
  if (selection) {
    selection.removeAllRanges();
    selection.addRange(range);
  }

  const limitedText = limitTextForInsert(pastedData);
  if (!limitedText) {
    return;
  }

  const inserted = document.execCommand('insertText', false, limitedText);
  if (!inserted) {
    range = resolveEditorRange();
    if (!range) {
      return;
    }
    if (selection) {
      selection.removeAllRanges();
      selection.addRange(range);
    }
    pastePlainTextWithRange(range, limitedText);
  }

  captureSelectionRange();
  handleInput();
};

const insertMentionByOption = (option, measurePrefix = '{') => {
  if (props.disabled || !divEditor.value || !option) {
    return false;
  }

  const range = resolveEditorRange();
  if (!range) {
    return false;
  }
  setFocus();

  const { scrollTop } = divEditor.value;
  const value = option.value ?? option.label ?? '';
  if (!value) return false;

  const mentionText = props.transformFn(option);
  if (!limitTextForInsert(mentionText)) {
    return false;
  }
  insertMention(
    range,
    mentionText,
    value,
    props.mentionClass,
    props.styleMap[measurePrefix]
  );
  handleInput();
  nextTick(() => {
    divEditor.value.scrollTop = scrollTop;
  });
  return true;
};

watch(
  () => props.modelValue,
  val => {
    if (isInput.value) {
      return;
    }
    if (divEditor.value && val !== divEditor.value.innerHTML) {
      divEditor.value.innerHTML = val;
    }
  }
);
onBeforeUnmount(() => {
  if (popperIns.value) {
    popperIns.value.destroy();
    popperIns.value = null;
  }
});
defineExpose({
  selectOption,
  insertMentionByOption,
  setFocus,
  divEditor,
  initEditor,
  onPaste,
  computedStyle
});

const editorWrap = ref(null);

const startResize = e => {
  e.preventDefault();

  const startY = e.clientY;
  const startHeight = editorWrap.value.offsetHeight;

  const handleMouseMove = moveEvent => {
    const deltaY = moveEvent.clientY - startY;
    const newHeight = startHeight + deltaY;

    const minHeight = 40;
    resizedHeight.value = Math.max(minHeight, newHeight);
  };

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('mouseup', handleMouseUp);

  onBeforeUnmount(() => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  });
};
</script>

<style lang="less" scoped>
.mentions-editor-wrap {
  width: 100%;
  border-radius: 8px;

  padding: 8px 11px;
  color: var(--active-font-color);
  height: 100%;
  border: 1px solid var(--border-neutral-color-secondary);
  white-space: pre-line;
  overflow-y: auto;
  display: flex;
  align-items: flex-start;
  position: relative;

  &:not(.disabled):focus-within {
    border: 1px solid var(--theme-color);
  }
  &:not(.disabled):hover {
    border: 1px solid var(--theme-color);
  }
  &.disabled {
    cursor: not-allowed;
    background-color: var(--form-disabled-background-color);
    .mentions-editor {
      cursor: not-allowed;
      -webkit-user-modify: read-only;
    }
    .resize-handle {
      display: none;
    }
  }
}

.mentions-editor {
  width: 100%;
  max-width: 100%;
  border: none;
  outline: none;
  min-height: 22px;
  font-size: 14px;
  height: 100%;
  overflow-y: auto;
  line-height: 21px;
  word-wrap: break-word;
  word-break: break-all;
  white-space: break-spaces;
  overflow-wrap: anywhere;
  flex-shrink: 1;
  position: relative;
  cursor: text;
  -webkit-user-modify: read-write-plaintext-only;
  &.disabled {
    -webkit-user-modify: unset;
    cursor: not-allowed;
  }
  &:empty:before {
    content: attr(data-placeholder);
    color: var(--placeholder-color);
  }
  &[contenteditable='false'] {
    cursor: not-allowed;
    -webkit-user-modify: read-only;
    user-modify: read-only;
  }
}

.popper-wrap {
  z-index: 1000;
}

.resize-handle {
  position: absolute;
  right: 2px;
  bottom: 2px;
  cursor: se-resize;
  color: var(--placeholder-color);

  &:hover {
    color: var(--theme-color);
  }
}
</style>
